/*
 * SubscriptionCancelled — landing page when Stripe returns because the
 * user cancelled the hosted checkout (or navigated away).
 *
 * Calm, non-shaming copy. Provides two clear next actions: return to the
 * paywall, or continue in the app if they still have trial time.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, LogIn } from 'lucide-react';
import { Button } from '../components/ui/button';

const COLORS = {
  bg: '#121212',
  card: '#1C1E21',
  gold: '#D4AF37',
  alabaster: '#FDFBF7',
  sand: '#D2B48C',
  fgSubtle: 'rgba(253, 251, 247, 0.72)',
  fgMuted: 'rgba(253, 251, 247, 0.52)',
  hairline: 'rgba(253, 251, 247, 0.08)',
};

export default function SubscriptionCancelled() {
  const navigate = useNavigate();
  return (
    <div
      data-testid="subscription-cancelled-page"
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: COLORS.bg, color: COLORS.alabaster }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-8 text-center"
        style={{
          background: COLORS.card,
          border: `1px solid ${COLORS.hairline}`,
          boxShadow: '0 24px 60px -20px rgba(0,0,0,0.55)',
        }}
      >
        <p
          className="text-[11px] uppercase tracking-[0.32em] mb-4"
          style={{ color: COLORS.sand }}
        >
          Selah Downloads
        </p>
        <h1
          className="text-2xl font-semibold mb-3"
          style={{ fontFamily: '"Playfair Display", serif' }}
        >
          No worries — nothing charged.
        </h1>
        <p className="text-sm mb-6" style={{ color: COLORS.fgSubtle }}>
          You stepped away from checkout. Whenever you&rsquo;re ready to
          continue, we&rsquo;ll be right here.
        </p>
        <p
          className="text-[13px] italic mb-8"
          style={{ color: COLORS.sand, fontFamily: '"Crimson Text", serif' }}
        >
          &ldquo;Be still, and know that I am God.&rdquo;
        </p>

        <div className="flex flex-col gap-3">
          <Button
            data-testid="subscription-cancelled-try-again"
            onClick={() => navigate('/login')}
            className="w-full h-11 rounded-xl font-semibold"
            style={{
              background: `linear-gradient(180deg, ${COLORS.gold} 0%, #B7912D 100%)`,
              color: '#1A1204',
              border: `1px solid ${COLORS.gold}`,
            }}
          >
            <LogIn size={15} className="mr-2" />
            Return to sign in
          </Button>
          <Link to="/">
            <Button
              data-testid="subscription-cancelled-home"
              variant="outline"
              className="w-full h-10 rounded-xl"
              style={{
                background: 'transparent',
                color: COLORS.fgSubtle,
                border: `1px solid ${COLORS.hairline}`,
              }}
            >
              <ArrowLeft size={14} className="mr-2" />
              Back to landing
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
